import { Link } from "react-router-dom";
import type { topics } from "@/mocks/topics";
import { memes } from "@/mocks/memes";
import { formatHeat } from "@/utils/format";

type Topic = (typeof topics)[number];

interface ArticleAsideProps {
  topic: Topic;
  related: Topic[];
}

export default function ArticleAside({ topic, related }: ArticleAsideProps) {
  const topicMemes = memes.filter((meme) => topic.memeIds.includes(meme.id));
  const hotList = [...related].sort((a, b) => b.heat - a.heat).slice(0, 4);

  return (
    <aside className="space-y-5 lg:sticky lg:top-28 lg:self-start" aria-label="专题侧栏">
      <nav className="rounded-lg border border-background-200 bg-background-50 p-5" aria-label="本页目录">
        <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
          <i className="ri-list-check-2 text-primary-500" aria-hidden="true"></i>
          本页目录
        </h2>
        <ol className="mt-4 space-y-2.5">
          {topic.sections.map((section, index) => (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
              >
                <span className="font-heading text-primary-500">{String(index + 1).padStart(2, "0")}</span>
                {section.heading}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {topicMemes.length > 0 ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
            <i className="ri-emotion-laugh-line text-primary-500" aria-hidden="true"></i>
            相关表情包
          </h2>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {topicMemes.map((meme) => (
              <Link
                key={meme.id}
                to="/memes"
                className="group overflow-hidden rounded-md border border-background-200 bg-background-100"
              >
                <span className="block aspect-square w-full overflow-hidden">
                  <img
                    src={meme.image}
                    alt={`${meme.title} 表情包`}
                    title={`${meme.title} - 513吃瓜表情包`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </span>
                <span className="block px-2 py-1.5 text-center text-xs text-foreground-600">{meme.title}</span>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      {hotList.length > 0 ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
            <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
            相关推荐
          </h2>
          <ol className="mt-4 space-y-3">
            {hotList.map((item) => (
              <li key={item.id} className="border-b border-background-200 pb-3 last:border-0 last:pb-0">
                <Link
                  to={`/topics/${item.id}`}
                  className="line-clamp-2 text-sm text-foreground-800 transition-colors hover:text-primary-700"
                >
                  {item.title}
                </Link>
                <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                  {item.categoryName} · {formatHeat(item.heat)}
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </aside>
  );
}